import { useState, useEffect, useCallback } from "react";
import {
  FaHandshake,
  FaStore,
  FaEdit,
  FaCheckCircle,
  FaUpload,
  FaTag,
  FaTimes,
  FaShieldAlt,
  FaUserTie,
  FaPlus
} from "react-icons/fa";
import StoreDetails from "./StoreDetails";
import { useApi } from "../context/useApi";
import SearchableMapPicker from "../context/MapPicker";

const StoresPanel = () => {
  const [expandedVendors, setExpandedVendors] = useState({});
  const [vendors, setVendors] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [toast, setToast] = useState({ message: "", type: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingVendors, setIsFetchingVendors] = useState(true);
  const [editingVendorId, setEditingVendorId] = useState(null);
  const [modalMode, setModalMode] = useState("addVendor");
  const [tagInput, setTagInput] = useState("");
  const { apiFetch } = useApi();
  const [selectedStore, setSelectedStore] = useState(null);
  const [newStore, setNewStore] = useState({
    store_name: "",
    description: "",
    store_type: "Offline",
    product_categories: [],
    store_image_url: "",
    gst: "",
    location: "",
    address: "",
    coords: null,
    vendor_id: "",
    vendor_name: "",
    is_active: true,
    tags: [],
    remarks: "",
  });
  const API_BASE = "https://admin-backend-fbuuh6fo3q-el.a.run.app/vendors";

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "" }), 3000);
  };
  const fetchVendorsWithStores = useCallback(async () => {
    setIsFetchingVendors(true);
    try {
      const res = await apiFetch(`${API_BASE}/vendors/stores`);
      if (!res) return;
      const data = await res.json();
      if (res.ok) {
        setVendors(data.vendors || []);
      } else {
        showToast(data.error || "Failed to fetch vendors", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Something went wrong while fetching vendors", "error");
    } finally {
      setIsFetchingVendors(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    fetchVendorsWithStores();
  }, [fetchVendorsWithStores]);

  const toggleExpand = (vendorId) => {
    setExpandedVendors((prev) => ({
      ...prev,
      [vendorId]: !prev[vendorId],
    }));
  };
  const [newVendor, setNewVendor] = useState({
    name: "",
    logo_url: "",
    business_type: "Online",
    vendor_type: "Single Store",
    industry_category: "",
    head_office_address: "",
    products_services: "",
    store_count: "",
    vendor_admin_id: "",
    vendor_registration_number: "",
    subscription_plan: "Free",
    is_active: false,
    is_listed: false,
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewVendor((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();

      reader.onloadend = () => {
        setNewVendor((prev) => ({
          ...prev,
          logo_url: reader.result,
        }));
      };

      reader.readAsDataURL(file);
    }
  };
  const handleStoreLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setNewStore((prev) => ({
          ...prev,
          store_image_url: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };
  const handleAddVendor = async () => {
    if (
      !newVendor.name ||
      !newVendor.business_type ||
      !newVendor.vendor_type ||
      !newVendor.industry_category ||
      !newVendor.head_office_address ||
      !newVendor.logo_url ||
      !newVendor.vendor_registration_number
    ) {
      showToast("Please fill all mandatory fields (*)", "error");
      return;
    }
    if (!newVendor.is_active || !newVendor.is_listed) {
      showToast("Please confirm the vendor is Active and Listed", "error");
      return;
    }

    setIsLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/add-vendor`, {
        method: "POST",
        body: JSON.stringify(newVendor),
      });
      if (!res) return;

      const data = await res.json();
      if (res.ok) {
        showToast(data.message || "Vendor added successfully!", "success");
        setIsModalOpen(false);

        const refreshed = await apiFetch(`${API_BASE}/vendors/stores`);
        if (!refreshed) return;
        const refreshedData = await refreshed.json();
        setVendors(refreshedData.vendors || []);

        setNewVendor({
          store_name: "",
          description: "",
          store_type: "Offline",
          product_categories: [],
          gst: "",
          location: "",
          address: "",
          is_active: false,
          tags: [],
          remarks: "",
          store_image_url: "",
        });
      } else {
        showToast(data.error || "Failed to add vendor", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Something went wrong! Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditVendor = async (vendorId, updatedVendor) => {
    setIsLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/update-vendor/${vendorId}`, {
        method: "PUT",
        body: JSON.stringify(updatedVendor),
      });
      if (!res) return;

      const data = await res.json();
      if (res.ok) {
        showToast(data.message || "Vendor updated successfully!", "success");

        const refreshed = await apiFetch(`${API_BASE}/vendors/stores`);
        if (!refreshed) return;
        const refreshedData = await refreshed.json();
        setVendors(refreshedData.vendors || []);

        setIsModalOpen(false);
        setEditingVendorId(null);
        setNewVendor({
          store_name: "",
          description: "",
          store_type: "Offline",
          product_categories: [],
          gst: "",
          location: "",
          address: "",
          is_active: false,
          tags: [],
          remarks: "",
          store_image_url: "",
        });
      } else {
        showToast(data.error || "Failed to update vendor", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Something went wrong! Please try again.", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddStore = async () => {
    if (!newStore.is_active) {
      showToast("Please mark 'Is Active'", "error");
      return;
    }
    if (
      !newStore.store_name ||
      !newStore.store_type ||
      !newStore.gst ||
      !newStore.store_image_url
    ) {
      showToast("Please fill all mandatory fields (*)", "error");
      return;
    }

    setIsLoading(true);
    try {
      const res = await apiFetch(`${API_BASE}/stores/add-store`, {
        method: "POST",
        body: JSON.stringify(newStore),
      });
      if (!res) return;

      const data = await res.json();
      if (res.ok) {
        showToast(data.message || "Store added successfully!", "success");

        const refreshed = await apiFetch(`${API_BASE}/vendors/stores`);
        if (!refreshed) return;
        const refreshedData = await refreshed.json();
        setVendors(refreshedData.vendors || []);

        setIsModalOpen(false);
        setNewStore({
          store_name: "",
          description: "",
          store_type: "Offline",
          product_categories: [],
          gst: "",
          location: "",
          address: "",
          is_active: false,
          tags: [],
          remarks: "",
          store_image_url: "",
        });
      } else {
        showToast(data.error || "Failed to add store", "error");
      }
    } catch (err) {
      console.error(err);
      showToast("Something went wrong!", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingVendorId(null);
    setNewVendor({
      name: "",
      logo_url: "",
      business_type: "Online",
      vendor_type: "Single Store",
      industry_category: "",
      head_office_address: "",
      services_offered: "",
      store_count: "",
      vendor_admin_id: "",
      vendor_registration_number: "",
      subscription_plan: "Free",
      is_active: false,
      is_listed: false,
    });
    setNewStore({
      store_name: "",
      description: "",
      store_type: "Offline",
      product_categories: [],
      gst: "",
      location: "",
      address: "",
      is_active: false,
      tags: [],
      remarks: "",
      store_image_url: "",
    });

    setTagInput("");
  };

  return (
    <>
      {!selectedStore ? (
        <>
          {" "}
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-10">
            <div>
              <h2 className="text-2xl font-semibold text-gray-800">Stores</h2>
              <p className="text-sm text-gray-500">
                Manage your registered stores, vendors, and business details.
              </p>
            </div>

            <button
              onClick={() => {
                setModalMode("addVendor");
                setEditingVendorId(null);
                setNewVendor({
                  name: "",
                  logo_url: "",
                  business_type: "Online",
                  vendor_type: "Single Store",
                  industry_category: "",
                  head_office_address: "",
                  services_offered: "",
                  store_count: "",
                  vendor_admin_id: "",
                  vendor_registration_number: "",
                  subscription_plan: "Free",
                  is_active: false,
                  is_listed: false,
                });
                setIsModalOpen(true);
              }}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white border bg-gray-900 rounded-lg hover:bg-gray-800"
            >
              <FaPlus />
              Add Vendor
            </button>
          </div>
          {/* Vendors List */}
          <div className="space-y-8">
            {isFetchingVendors ? (
              <>
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="border-2 border-gray-200 rounded-xl p-5 animate-pulse"
                  >
                    {/* Brand Header */}
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 border-2 border-gray-300 rounded-lg"></div>
                        <div className="h-5 w-32 border border-gray-300 rounded"></div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <div className="h-9 w-32 border border-gray-300 rounded-lg"></div>
                        <div className="h-9 w-28 border border-gray-300 rounded-lg"></div>
                      </div>
                    </div>

                    {/* Product Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                      {[1, 2, 3].map((j) => (
                        <div
                          key={j}
                          className="flex flex-col items-center border border-gray-200 rounded-lg p-4"
                        >
                          <div className="w-40 h-28 border border-gray-300 rounded-lg mb-3"></div>
                          <div className="h-4 w-24 border border-gray-300 rounded"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </>
            ) : (
              vendors.map((vendor) => {
                const isExpanded = expandedVendors[vendor.vendor_id];
                const visibleStores = isExpanded
                  ? vendor.stores
                  : (vendor.stores || []).slice(0, 3);

                return (
                  <div
                    key={vendor.vendor_id}
                    className="border rounded-xl shadow-md p-5 bg-white"
                  >
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 gap-4">
                      <div className="flex items-center gap-3">
                        <img
                          src={vendor.logo_url}
                          alt={`${vendor.name} logo`}
                          className="w-10 h-10 object-contain rounded-lg"
                        />
                        <h4 className="text-lg font-semibold">{vendor.name}</h4>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <button
                          onClick={() => {
                            setModalMode("addStore");
                            setNewStore({
                              ...newStore,
                              vendor_id: vendor.vendor_id,
                              vendor_name: vendor.name,
                            });
                            setIsModalOpen(true);
                          }}
                          className="px-3 py-1.5 text-sm sm:text-base rounded-lg flex items-center justify-center gap-2  text-white border bg-gray-900  hover:bg-gray-800"
                        >
                          <FaPlus /> Add Store
                        </button>

                        <button
                          onClick={() => {
                            setModalMode("editVendor");
                            setNewVendor(vendor);
                            setIsModalOpen(true);
                            setEditingVendorId(vendor.vendor_id);
                          }}
                          className="px-3 py-1.5 text-sm sm:text-base rounded-lg flex items-center justify-center gap-2 border border-black text-blue-600 hover:bg-blue-50 transition"
                        >
                          Edit Vendor
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                      {visibleStores.map((store) => (
                        <div
                          key={store.store_id}
                          onClick={() => setSelectedStore(store)}
                          className="flex flex-col items-center border rounded-lg p-4 hover:shadow-md transition"
                        >
                          <div className="w-40 h-28 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center mb-3">
                            <img
                              src={store.store_image_url}
                              alt={store.store_name}
                              className="w-full h-full object-contain rounded-lg"
                            />
                          </div>
                          <h5 className="font-medium text-center">
                            {store.store_name}
                          </h5>
                        </div>
                      ))}
                    </div>

                    {vendor.stores && vendor.stores.length > 3 && (
                      <div className="flex justify-center mt-4">
                        <button
                          onClick={() => toggleExpand(vendor.vendor_id)}
                          className="px-4 py-2 text-blue-600 hover:underline"
                        >
                          {isExpanded ? "Show Less" : "View All"}
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </>
      ) : (
        <StoreDetails
          store={selectedStore}
          goBack={() => setSelectedStore(null)}
        />
      )}
      {/* Add Vendor Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
                {modalMode === "addVendor" && (
                  <>
                    <FaHandshake className="text-amber-600" />
                    Add New Vendor
                  </>
                )}
                {modalMode === "editVendor" && (
                  <>
                    <FaEdit className="text-amber-600" />
                    Edit Vendor
                  </>
                )}
                {modalMode === "addStore" && (
                  <>
                    <FaStore className="text-blue-600" />
                    Add Store
                  </>
                )}
              </h3>
              <button
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FaTimes className="text-gray-500" />
              </button>
            </div>

            <div className="p-6">
              {modalMode === "addStore" ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Store Name */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Store Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newStore.store_name}
                        onChange={(e) =>
                          setNewStore({
                            ...newStore,
                            store_name: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all"
                        placeholder="Enter store name"
                      />
                    </div>

                    {/* Store Type */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Store Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={newStore.store_type}
                        onChange={(e) =>
                          setNewStore({
                            ...newStore,
                            store_type: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all"
                      >
                        <option value="Online">Online</option>
                        <option value="Offline">Offline</option>
                        <option value="Hybrid">Hybrid</option>
                      </select>
                    </div>

                    {/* GST */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        GST <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={newStore.gst}
                        onChange={(e) =>
                          setNewStore({ ...newStore, gst: e.target.value })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all"
                        placeholder="Enter GST number"
                      />
                    </div>

                    {/* Product Categories */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Product Categories
                      </label>
                      <input
                        type="text"
                        value={newStore.product_categories.join(", ")}
                        onChange={(e) =>
                          setNewStore({
                            ...newStore,
                            product_categories: e.target.value
                              .split(",")
                              .map((cat) => cat.trim()),
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all"
                        placeholder="e.g., Electronics, Clothing"
                      />
                    </div>

                    {/* Store Logo */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Store Logo <span className="text-red-500">*</span>
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleStoreLogoUpload}
                          className="hidden"
                          id="store-logo-upload"
                        />
                        <label
                          htmlFor="store-logo-upload"
                          className="cursor-pointer flex flex-col items-center"
                        >
                          {newStore.store_image_url ? (
                            <img
                              src={newStore.store_image_url}
                              alt="Store Preview"
                              className="h-24 w-24 object-contain rounded-lg"
                            />
                          ) : (
                            <>
                              <FaUpload className="text-3xl text-gray-400 mb-2" />
                              <span className="text-sm text-gray-600">
                                Click to upload store logo
                              </span>
                            </>
                          )}
                        </label>
                      </div>
                    </div>

                    {/* Tags */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Tags
                      </label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <FaTag className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all"
                            placeholder="Type a tag and press Enter"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            if (
                              tagInput.trim() &&
                              !newStore.tags.includes(tagInput.trim())
                            ) {
                              setNewStore({
                                ...newStore,
                                tags: [...newStore.tags, tagInput.trim()],
                              });
                              setTagInput("");
                            }
                          }}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Add Tag
                        </button>
                      </div>
                      {newStore.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {newStore.tags.map((tag, idx) => (
                            <span
                              key={idx}
                              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                            >
                              {tag}
                              <button
                                type="button"
                                onClick={() =>
                                  setNewStore({
                                    ...newStore,
                                    tags: newStore.tags.filter(
                                      (t) => t !== tag
                                    ),
                                  })
                                }
                                className="ml-2 text-blue-600 hover:text-blue-800"
                              >
                                <FaTimes className="text-xs" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Store Location (only for Offline/Hybrid) */}
                    {newStore.store_type !== "Online" && (
                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Store Location <span className="text-red-500">*</span>
                        </label>
                        <div className="border rounded-lg overflow-hidden h-64">
                          <SearchableMapPicker
                            value={
                              newStore.coords
                                ? [newStore.coords.lat, newStore.coords.lng]
                                : [20.5937, 78.9629]
                            }
                            onChange={(coords) => {
                              setNewStore({
                                ...newStore,
                                coords,
                                location: `https://www.google.com/maps?q=${coords.lat},${coords.lng}`,
                              });
                            }}
                          />
                        </div>
                        {newStore.location && (
                          <p className="text-xs text-gray-500 mt-2 break-all">
                            {newStore.location}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Description */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Description
                      </label>
                      <textarea
                        value={newStore.description}
                        onChange={(e) =>
                          setNewStore({
                            ...newStore,
                            description: e.target.value,
                          })
                        }
                        rows="3"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all"
                        placeholder="Enter store description"
                      />
                    </div>

                    {/* Remarks */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Remarks
                      </label>
                      <textarea
                        value={newStore.remarks}
                        onChange={(e) =>
                          setNewStore({ ...newStore, remarks: e.target.value })
                        }
                        rows="3"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent transition-all"
                        placeholder="Additional notes about the store"
                      />
                    </div>

                    {/* Is Active Checkbox */}
                    <div className="md:col-span-2">
                      <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={newStore.is_active}
                            onChange={(e) =>
                              setNewStore({
                                ...newStore,
                                is_active: e.target.checked,
                              })
                            }
                            className="w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                          />
                          <span className="font-medium text-gray-700">
                            <FaCheckCircle className="inline mr-1 text-green-600" />
                            Store is Active
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between pt-6 border-t">
                    <button
                      onClick={closeModal}
                      className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleAddStore}
                      disabled={isLoading}
                      className={`px-6 py-2 rounded-lg text-white flex items-center gap-2 ${
                        isLoading
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-black hover:bg-grey-200"
                      } transition-colors`}
                    >
                      {isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <FaCheckCircle />
                          Add Store
                        </>
                      )}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Vendor Name */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Vendor Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={newVendor.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2  focus:border-transparent transition-all"
                        placeholder="Enter vendor name"
                      />
                    </div>

                    {/* Business Type */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Business Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="business_type"
                        value={newVendor.business_type}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2  focus:border-transparent transition-all"
                      >
                        <option value="Online">Online</option>
                        <option value="Offline">Offline</option>
                        <option value="Both">Both</option>
                      </select>
                    </div>

                    {/* Vendor Type */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Vendor Type <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="vendor_type"
                        value={newVendor.vendor_type}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2  focus:border-transparent transition-all"
                      >
                        <option value="Single Store">Single Store</option>
                        <option value="Multi-Store">Multi-Store</option>
                      </select>
                    </div>

                    {/* Store Count */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Store Count
                      </label>
                      <input
                        type="number"
                        name="store_count"
                        value={newVendor.store_count}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2  focus:border-transparent transition-all"
                        placeholder="Number of stores"
                      />
                    </div>

                    {/* Industry Category */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Industry Category{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="industry_category"
                        value={newVendor.industry_category}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2  focus:border-transparent transition-all"
                        placeholder="e.g., Retail, Food & Beverage"
                      />
                    </div>

                    {/* Services Offered */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Services Offered
                      </label>
                      <input
                        type="text"
                        name="services_offered"
                        value={newVendor.services_offered}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2  focus:border-transparent transition-all"
                        placeholder="e.g., Electronics, Clothing"
                      />
                    </div>

                    {/* Head Office Address */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Head Office Address{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="head_office_address"
                        value={newVendor.head_office_address}
                        onChange={handleInputChange}
                        rows="3"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2  focus:border-transparent transition-all"
                        placeholder="Complete address"
                      />
                    </div>

                    {/* Registration Number */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Registration Number{" "}
                        <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="vendor_registration_number"
                        value={newVendor.vendor_registration_number}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2  focus:border-transparent transition-all"
                        placeholder="Vendor registration number"
                      />
                    </div>

                    {/* Subscription Plan */}
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Subscription Plan
                      </label>
                      <select
                        name="subscription_plan"
                        value={newVendor.subscription_plan}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2  focus:border-transparent transition-all"
                      >
                        <option value="Free">Free</option>
                        <option value="Standard">Standard</option>
                        <option value="Premium">Premium</option>
                      </select>
                    </div>

                    {/* Admin ID */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        <FaUserTie className="inline mr-1" />
                        Vendor Admin ID
                      </label>
                      <input
                        type="text"
                        name="vendor_admin_id"
                        value={newVendor.vendor_admin_id}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2  focus:border-transparent transition-all"
                        placeholder="Admin mobile number"
                      />
                    </div>

                    {/* Logo Upload */}
                    <div className="md:col-span-2">
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Vendor Logo <span className="text-red-500">*</span>
                      </label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-amber-400 transition-colors">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleLogoUpload}
                          className="hidden"
                          id="vendor-logo-upload"
                        />
                        <label
                          htmlFor="vendor-logo-upload"
                          className="cursor-pointer flex flex-col items-center"
                        >
                          {newVendor.logo_url ? (
                            <img
                              src={newVendor.logo_url}
                              alt="Logo Preview"
                              className="h-24 w-24 object-contain rounded-lg"
                            />
                          ) : (
                            <>
                              <FaUpload className="text-3xl text-gray-400 mb-2" />
                              <span className="text-sm text-gray-600">
                                Click to upload vendor logo
                              </span>
                            </>
                          )}
                        </label>
                      </div>
                    </div>

                    {/* Checkboxes */}
                    <div className="md:col-span-2">
                      <div className="flex items-center gap-6 p-4 bg-gray-50 rounded-lg">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            name="is_active"
                            checked={newVendor.is_active}
                            onChange={handleInputChange}
                            className="w-5 h-5 text-amber-600 rounded "
                          />
                          <span className="font-medium text-gray-700">
                            <FaCheckCircle className="inline mr-1 text-green-600" />
                            Active Vendor
                          </span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            name="is_listed"
                            checked={newVendor.is_listed}
                            onChange={handleInputChange}
                            className="w-5 h-5 text-amber-600 rounded "
                          />
                          <span className="font-medium text-gray-700">
                            <FaShieldAlt className="inline mr-1 text-blue-600" />
                            Listed on Stock Exchange
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-between pt-6 border-t">
                    <button
                      onClick={closeModal}
                      className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={
                        modalMode === "editVendor"
                          ? () => handleEditVendor(editingVendorId, newVendor)
                          : handleAddVendor
                      }
                      disabled={isLoading}
                      className={`px-6 py-2 rounded-lg text-white flex items-center gap-2 ${
                        isLoading
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-black hover:bg-gray-200"
                      } transition-colors`}
                    >
                      {isLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                          {modalMode === "editVendor"
                            ? "Updating..."
                            : "Saving..."}
                        </>
                      ) : (
                        <>
                          <FaCheckCircle />
                          {modalMode === "editVendor"
                            ? "Update Vendor"
                            : "Add Vendor"}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {toast.message && (
        <div
          className={`fixed top-5 right-5 px-4 py-2 rounded shadow-lg text-white z-50 transition ${
            toast.type === "success" ? "bg-green-500" : "bg-red-500"
          }`}
        >
          {toast.message}
        </div>
      )}
    </>
  );
};

export default StoresPanel;
